import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  LinearProgress,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import DownloadIcon from '@mui/icons-material/Download';
import FilterListIcon from '@mui/icons-material/FilterList';
import {
  ProductImageValidationAssetResult,
  ProductImageValidationCheck,
  ProductImageValidationJob,
  ProductImageValidationRun,
} from './types';

type StatusFilter = 'all' | 'fail' | 'pass';

interface ProductImageValidationRunDialogProps {
  open: boolean;
  title?: string;
  job: ProductImageValidationJob | null;
  run: ProductImageValidationRun | null;
  error: string | null;
  starting?: boolean;
  loadingRun?: boolean;
  onClose: () => void;
  onDownload: (format: 'json' | 'csv' | 'txt') => void;
}

function statusIcon(status: 'pass' | 'fail' | 'error', size: number = 18) {
  if (status === 'pass') return <CheckCircleIcon sx={{ fontSize: size, color: '#2e7d32' }} />;
  if (status === 'fail') return <CancelIcon sx={{ fontSize: size, color: '#d32f2f' }} />;
  return <WarningAmberIcon sx={{ fontSize: size, color: '#ed6c02' }} />;
}

function checkIcon(check: ProductImageValidationCheck) {
  return (
    <Tooltip title={check.message} arrow placement="top">
      <Box sx={{ display: 'inline-flex', alignItems: 'center', cursor: 'help' }}>
        {statusIcon(check.status, 16)}
      </Box>
    </Tooltip>
  );
}

const CHECK_LABELS: Record<string, string> = {
  IN_PRODUCTS_CATEGORY: 'Produkter',
  IN_WINE_CATEGORY: 'Vin',
  SOURCE_EXISTS: 'Fil finns',
  INVALID_FORMAT: 'JPG',
  COLORSPACE_RGB: 'RGB',
  ICC_NOT_ADOBE_RGB_1998: 'Adobe RGB',
  DPI_NOT_300: '300 DPI',
  AT_LEAST_ONE_SIDE_MIN_PX: 'Pixlar',
  CLIPPING_PATH_1: 'Path 1',
  INSPECTION_ERROR: 'Analys',
  ASSET_NOT_FOUND: 'Saknas',
};

function shortCheckLabel(code: string): string {
  return CHECK_LABELS[code] || code;
}

const AssetRow: React.FC<{ result: ProductImageValidationAssetResult }> = ({ result }) => {
  const [expanded, setExpanded] = useState(false);
  const failedChecks = result.checks.filter((c) => c.status !== 'pass');
  const passedChecks = result.checks.filter((c) => c.status === 'pass');
  const displayName = result.productName || result.title || result.fileName;

  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: result.status === 'pass' ? '#c8e6c9' : result.status === 'error' ? '#fff3e0' : '#ffcdd2',
        borderRadius: 1.5,
        mb: 1,
        backgroundColor: result.status === 'pass' ? '#f1f8e9' : result.status === 'error' ? '#fff8e1' : '#fff5f5',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 1.5,
          py: 1,
          cursor: 'pointer',
          '&:hover': { backgroundColor: 'rgba(0,0,0,0.02)' },
        }}
        onClick={() => setExpanded((prev) => !prev)}
      >
        {statusIcon(result.status, 22)}

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {displayName}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}>
            {result.fileName}
            {result.articleNumber ? ` — Art.nr: ${result.articleNumber}` : ''}
          </Typography>
        </Box>

        <Stack direction="row" spacing={0.5} sx={{ flexShrink: 0 }}>
          {result.checks.map((check) => (
            <Tooltip key={check.code} title={`${shortCheckLabel(check.code)}: ${check.message}`} arrow placement="top">
              <Box sx={{ display: 'inline-flex' }}>
                {statusIcon(check.status, 16)}
              </Box>
            </Tooltip>
          ))}
        </Stack>

        <IconButton size="small" sx={{ ml: 0.5 }}>
          {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
        </IconButton>
      </Box>

      <Collapse in={expanded}>
        <Divider />
        <Box sx={{ px: 2, py: 1.5 }}>
          {failedChecks.length > 0 && (
            <Box sx={{ mb: passedChecks.length > 0 ? 1.5 : 0 }}>
              <Typography variant="caption" sx={{ fontWeight: 700, color: '#d32f2f', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Underkänd
              </Typography>
              {failedChecks.map((check) => (
                <Box key={check.code} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mt: 0.5 }}>
                  {checkIcon(check)}
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{check.label}</Typography>
                    <Typography variant="caption" color="text.secondary">{check.message}</Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          )}
          {passedChecks.length > 0 && (
            <Box>
              <Typography variant="caption" sx={{ fontWeight: 700, color: '#2e7d32', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Godkänd
              </Typography>
              {passedChecks.map((check) => (
                <Box key={check.code} sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.3 }}>
                  {checkIcon(check)}
                  <Typography variant="body2" color="text.secondary">{check.label}</Typography>
                </Box>
              ))}
            </Box>
          )}
          {(result.width || result.height) && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              Dimensioner: {result.width} x {result.height} px
              {result.longestSidePx ? ` (längsta sida ${result.longestSidePx} px)` : ''}
              {result.dpiX ? ` • ${result.dpiX} DPI` : ''}
              {result.iccProfileName ? ` • ICC: ${result.iccProfileName}` : ''}
              {result.clippingPathName ? ` • Urklippsbana: ${result.clippingPathName}` : ''}
            </Typography>
          )}
        </Box>
      </Collapse>
    </Box>
  );
};

const ProductImageValidationRunDialog: React.FC<ProductImageValidationRunDialogProps> = ({
  open,
  title = 'Bildkontroll Validoo',
  job,
  run,
  error,
  starting = false,
  loadingRun = false,
  onClose,
  onDownload,
}) => {
  const isRunning = starting || Boolean(job && !job.done);
  const [filter, setFilter] = useState<StatusFilter>('all');

  const progressPercent = job && job.totalAssets > 0
    ? Math.min(100, Math.round((job.current / job.totalAssets) * 100))
    : 0;

  const filteredResults = useMemo(() => {
    if (!run) return [];
    if (filter === 'all') return run.results;
    if (filter === 'fail') return run.results.filter((r) => r.status !== 'pass');
    return run.results.filter((r) => r.status === 'pass');
  }, [run, filter]);

  const allPassed = run && run.failedCount === 0 && run.errorCount === 0;

  return (
    <Dialog
      open={open}
      onClose={() => { if (!isRunning) onClose(); }}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { maxHeight: '90vh' } }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          {run && statusIcon(allPassed ? 'pass' : 'fail', 28)}
          <Typography variant="h6" component="span" sx={{ fontWeight: 700 }}>{title}</Typography>
        </Box>
      </DialogTitle>

      <DialogContent dividers sx={{ px: 2.5 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        )}

        {starting && (
          <Box sx={{ py: 3, textAlign: 'center' }}>
            <LinearProgress />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>Startar körning...</Typography>
          </Box>
        )}

        {job && !job.done && (
          <Box sx={{ py: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {job.current} av {job.totalAssets} bilder
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, color: '#072f53' }}>{progressPercent}%</Typography>
            </Box>
            <LinearProgress
              variant={job.totalAssets > 0 ? 'determinate' : 'indeterminate'}
              value={progressPercent}
              sx={{ height: 8, borderRadius: 4, mb: 1 }}
            />
            <Stack direction="row" spacing={2}>
              <Typography variant="caption" sx={{ color: '#2e7d32' }}>
                <CheckCircleIcon sx={{ fontSize: 13, verticalAlign: 'middle', mr: 0.3 }} />
                {job.passCount}
              </Typography>
              <Typography variant="caption" sx={{ color: '#d32f2f' }}>
                <CancelIcon sx={{ fontSize: 13, verticalAlign: 'middle', mr: 0.3 }} />
                {job.failedCount}
              </Typography>
              {job.errorCount > 0 && (
                <Typography variant="caption" sx={{ color: '#ed6c02' }}>
                  <WarningAmberIcon sx={{ fontSize: 13, verticalAlign: 'middle', mr: 0.3 }} />
                  {job.errorCount}
                </Typography>
              )}
            </Stack>
            {job.currentFileName && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                Analyserar: {job.currentFileName}
              </Typography>
            )}
          </Box>
        )}

        {loadingRun && !starting && (
          <Box sx={{ py: 3, textAlign: 'center' }}>
            <LinearProgress />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>Laddar resultat...</Typography>
          </Box>
        )}

        {run && !loadingRun && (
          <>
            <Box sx={{
              display: 'flex',
              gap: 2,
              flexWrap: 'wrap',
              alignItems: 'center',
              mb: 2,
              p: 2,
              borderRadius: 2,
              backgroundColor: allPassed ? '#e8f5e9' : '#fce4ec',
            }}>
              <Box sx={{ flex: 1, minWidth: 200 }}>
                <Typography variant="h5" sx={{ fontWeight: 700, color: allPassed ? '#2e7d32' : '#c62828' }}>
                  {allPassed ? 'Alla bilder godkända' : `${run.failedCount + run.errorCount} av ${run.totalAssets} bilder underkända`}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {new Date(run.startedAt).toLocaleString('sv-SE')} — {new Date(run.finishedAt).toLocaleString('sv-SE')}
                </Typography>
              </Box>
              <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                <Chip
                  icon={<CheckCircleIcon />}
                  label={`${run.passCount} godkända`}
                  size="small"
                  sx={{ backgroundColor: '#c8e6c9', fontWeight: 600 }}
                />
                {run.failedCount > 0 && (
                  <Chip
                    icon={<CancelIcon />}
                    label={`${run.failedCount} underkända`}
                    size="small"
                    sx={{ backgroundColor: '#ffcdd2', fontWeight: 600 }}
                  />
                )}
                {run.errorCount > 0 && (
                  <Chip
                    icon={<WarningAmberIcon />}
                    label={`${run.errorCount} fel`}
                    size="small"
                    sx={{ backgroundColor: '#fff3e0', fontWeight: 600 }}
                  />
                )}
                <Chip
                  label={`Pixelkrav: ${run.settings.minRequiredSidePx} px`}
                  size="small"
                  variant="outlined"
                />
              </Stack>
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#072f53' }}>
                Resultat ({filteredResults.length} {filteredResults.length === 1 ? 'bild' : 'bilder'})
              </Typography>
              <Stack direction="row" spacing={0.5}>
                <Tooltip title="Visa alla" arrow>
                  <Chip
                    icon={<FilterListIcon />}
                    label="Alla"
                    size="small"
                    variant={filter === 'all' ? 'filled' : 'outlined'}
                    onClick={() => setFilter('all')}
                    sx={{ cursor: 'pointer' }}
                  />
                </Tooltip>
                <Tooltip title="Visa bara underkända" arrow>
                  <Chip
                    icon={<CancelIcon />}
                    label="Underkända"
                    size="small"
                    variant={filter === 'fail' ? 'filled' : 'outlined'}
                    color="error"
                    onClick={() => setFilter('fail')}
                    sx={{ cursor: 'pointer' }}
                  />
                </Tooltip>
                <Tooltip title="Visa bara godkända" arrow>
                  <Chip
                    icon={<CheckCircleIcon />}
                    label="Godkända"
                    size="small"
                    variant={filter === 'pass' ? 'filled' : 'outlined'}
                    color="success"
                    onClick={() => setFilter('pass')}
                    sx={{ cursor: 'pointer' }}
                  />
                </Tooltip>
              </Stack>
            </Box>

            <Box sx={{ maxHeight: 420, overflowY: 'auto', pr: 0.5 }}>
              {filteredResults.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                  Inga bilder matchar filtret.
                </Typography>
              ) : (
                filteredResults.map((result) => (
                  <AssetRow key={result.assetId} result={result} />
                ))
              )}
            </Box>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 2.5, py: 1.5, justifyContent: 'space-between' }}>
        <Stack direction="row" spacing={1}>
          {run && (
            <>
              <Button size="small" startIcon={<DownloadIcon />} onClick={() => onDownload('csv')}>CSV</Button>
              <Button size="small" startIcon={<DownloadIcon />} onClick={() => onDownload('txt')}>TXT</Button>
              <Button size="small" startIcon={<DownloadIcon />} onClick={() => onDownload('json')}>JSON</Button>
            </>
          )}
        </Stack>
        <Button onClick={onClose} disabled={isRunning} variant="contained" sx={{ backgroundColor: '#072f53', '&:hover': { backgroundColor: '#0a3d6b' } }}>
          Stäng
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ProductImageValidationRunDialog;
